import React from 'react';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Users, ShieldCheck, Trash2, CheckCircle, XCircle, Mail, Phone, Globe, Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AdminAttendeesTab = ({ buyers, verifyUser, openCancelModal, onDeleteUser }) => {
  const { t } = useTranslation();

  const renderCompanyInfo = (user) => {
    const ci = user.company_info;
    if (!ci) return <span className="text-slate-400">No company info</span>;
    return (
      <div className="text-sm space-y-1">
        <p><strong>{ci.company_name}</strong></p>
        <p className="text-slate-600">{ci.street_address}, {ci.city}, {ci.state_province} {ci.postal_code}</p>
        <p className="text-slate-600">{ci.country}</p>
        <p className="text-slate-600"><Phone className="inline h-3 w-3 mr-1" />{ci.phone}</p>
        <p className="text-slate-600"><Mail className="inline h-3 w-3 mr-1" />{ci.company_email}</p>
        {ci.website && <p className="text-blue-600"><Globe className="inline h-3 w-3 mr-1" />{ci.website}</p>}
        <p className="text-slate-500 text-xs mt-1">Industry: {ci.industry}</p>
        <p className="text-slate-500 text-xs">{ci.description}</p>
      </div>
    );
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <h2 className="text-lg font-bold text-slate-900 mb-4">Registered Attendees</h2>
      {buyers.length === 0 ? (
        <div className="text-center py-8">
          <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">No attendees yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('dashboard.name')} / {t('dashboard.email')}</TableHead>
                <TableHead>{t('dashboard.company')}</TableHead>
                <TableHead>{t('dashboard.registered_events')}</TableHead>
                <TableHead>{t('dashboard.my_interactions')}</TableHead>
                <TableHead>{t('dashboard.status')}</TableHead>
                <TableHead>{t('dashboard.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {buyers.map((buyer) => (
                <TableRow key={buyer.id} className="hover:bg-slate-50">
                  <TableCell>
                    <div>
                      <p className="font-medium">{buyer.name}</p>
                      <p className="text-sm text-slate-500">{buyer.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs">{renderCompanyInfo(buyer)}</TableCell>
                  <TableCell>
                    {buyer.registrations?.length > 0 ? (
                      <div className="space-y-1">
                        {buyer.registrations.map((reg, idx) => (
                          <div key={idx} className="text-sm flex items-center gap-2">
                            <span className="font-medium">{reg.event?.name || 'Unknown'}</span>
                            <span className={`px-1.5 py-0.5 rounded text-xs ${reg.payment_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {reg.payment_status}
                            </span>
                            <Button size="sm" variant="ghost" className="h-6 px-1 text-red-500 hover:text-red-700"
                              onClick={() => openCancelModal(buyer, reg.event_id)}
                              title={`Remove from ${reg.event?.name || 'event'}`}
                              data-testid={`delete-registration-${reg.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : <span className="text-slate-400">No events</span>}
                  </TableCell>
                  <TableCell>
                    {buyer.interactions?.length > 0 ? (
                      <div className="space-y-1">
                        {buyer.interactions.slice(0, 2).map((int, idx) => (
                          <div key={idx} className="text-xs">
                            <span className={`px-1.5 py-0.5 rounded ${int.status === 'on_call' ? 'bg-green-100 text-green-700' : int.status === 'in_queue' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                              {int.status?.replace('_', ' ')}
                            </span>
                          </div>
                        ))}
                        {buyer.interactions.length > 2 && <span className="text-xs text-slate-400">+{buyer.interactions.length - 2} more</span>}
                      </div>
                    ) : <span className="text-slate-400">None</span>}
                  </TableCell>
                  <TableCell>
                    {buyer.is_verified ? (
                      <span className="flex items-center text-green-600 text-sm"><ShieldCheck className="h-4 w-4 mr-1" />Verified</span>
                    ) : (
                      <span className="text-amber-600 text-sm">Pending</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {!buyer.is_verified && (
                        <Button size="sm" variant="outline" className="text-green-600" onClick={() => verifyUser(buyer.id)} title="Verify">
                          <CheckCircle className="h-3 w-3" />
                        </Button>
                      )}
                      {buyer.registrations?.length > 0 && (
                        <Button size="sm" variant="outline" className="text-amber-600" 
                          onClick={() => openCancelModal(buyer, buyer.registrations[0]?.event_id)} title="Cancel Registration">
                          <Ban className="h-3 w-3" />
                        </Button>
                      )}
                      <Button size="sm" variant="destructive" onClick={() => onDeleteUser(buyer)} title="Delete User">
                        <XCircle className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default AdminAttendeesTab;
